import React, { useState } from 'react';
import {
  Grid,
  Stack,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Box,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  useTheme,
  Avatar,
} from '@mui/material';
import MainCard from 'components/MainCard';
import { Star, WorkspacePremium, RocketLaunch } from '@mui/icons-material';

const planData = {
  monthly: [
    {
      name: 'Free',
      price: 0,
      limit: 5,
      description: 'Add up to 5 yogas',
      color: 'default',
      icon: <Star color="primary" fontSize="large" />,
    },
    {
      name: 'Pro',
      price: 10,
      limit: 50,
      description: 'Add up to 50 yogas',
      color: 'primary',
      icon: <RocketLaunch color="primary" fontSize="large" />,
    },
    {
      name: 'Premium',
      price: 15,
      limit: 80,
      description: 'Add up to 80 yogas',
      color: 'secondary',
      icon: <WorkspacePremium color="secondary" fontSize="large" />,
    },
  ],
  yearly: [
    {
      name: 'Free',
      price: 0,
      limit: 5,
      description: 'Add up to 5 yogas',
      color: 'default',
      icon: <Star color="primary" fontSize="large" />,
    },
    {
      name: 'Pro',
      price: 100,
      limit: 600,
      description: 'Add up to 600 yogas',
      color: 'primary',
      icon: <RocketLaunch color="primary" fontSize="large" />,
    },
    {
      name: 'Premium',
      price: 150,
      limit: 960,
      description: 'Add up to 960 yogas',
      color: 'secondary',
      icon: <WorkspacePremium color="secondary" fontSize="large" />,
    },
  ],
};

// Mock current coach data
const currentPlanName = 'Pro'; // For demo, set to 'Pro'
const usage = 3; // Number of yogas created
const currentPlanType = 'monthly'; // For demo, set to 'monthly'

// Mock subscription history
const subscriptionHistory = [
  { plan: 'Free', start: '2024-01-01', end: '2024-03-01', status: 'Expired' },
  { plan: 'Pro', start: '2024-03-02', end: '2024-06-01', status: 'Expired' },
  { plan: 'Premium', start: '2024-06-02', end: 'Present', status: 'Active' },
];

const partnerLogos = [
  'Spotify',
  'Zendesk',
  'Uber',
  'Checkr',
  'Unity',
  'Miro',
];

function Subscription() {
  const theme = useTheme();
  const [planType, setPlanType] = useState('monthly');
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);

  const plans = planData[planType];
  const currentPlan = plans.find((p) => p.name === currentPlanName) || plans[0];

  const handleSend = () => {
    if (message.trim()) {
      setSent(true);
      setTimeout(() => setSent(false), 2000);
      setMessage('');
    }
  };

  const handlePlanType = (event, newType) => {
    if (newType) setPlanType(newType);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        p: { xs: 1, md: 3 },
        background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.secondary.light} 100%)`,
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            textAlign: 'center',
            mb: 2,
            color: theme.palette.text.primary,
          }}
        >
          Select The Best Plan For Your Needs
        </Typography>
        <Stack direction="row" justifyContent="center" alignItems="center" sx={{ mb: 4 }}>
          <ToggleButtonGroup
            value={planType}
            exclusive
            onChange={handlePlanType}
            sx={{ background: theme.palette.background.paper, borderRadius: 2, boxShadow: 1 }}
          >
            <ToggleButton value="monthly" sx={{ fontWeight: 600 }}>Monthly Plan</ToggleButton>
            <ToggleButton value="yearly" sx={{ fontWeight: 600 }}>Yearly Plan</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Grid container spacing={3} justifyContent="center" alignItems="stretch">
          {plans.map((plan) => (
            <Grid item xs={12} sm={6} md={4} key={plan.name}>
              <Paper
                elevation={plan.name === currentPlan.name ? 8 : 2}
                sx={{
                  p: 3,
                  borderRadius: 4,
                  background: plan.name === 'Premium'
                    ? `linear-gradient(135deg, ${theme.palette.secondary.light} 0%, ${theme.palette.primary.light} 100%)`
                    : plan.name === 'Pro'
                    ? `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`
                    : theme.palette.background.paper,
                  color: plan.name === 'Premium' ? theme.palette.common.white : 'inherit',
                  boxShadow: plan.name === currentPlan.name ? `0 8px 32px 0 ${theme.palette.primary.main}22` : undefined,
                  border: plan.name === currentPlan.name ? `2px solid ${theme.palette.primary.main}` : 'none',
                  position: 'relative',
                  minHeight: 340,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  transition: 'all 0.3s',
                }}
              >
                <Box sx={{ mb: 2 }}>{plan.icon}</Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  {plan.name}
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
                  {plan.price === 0 ? 'Free' : `$${plan.price}`}
                  <Typography component="span" variant="body1" sx={{ fontWeight: 400, ml: 1 }}>
                    {plan.price !== 0 && (planType === 'monthly' ? '/mo' : '/yr')}
                  </Typography>
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>{plan.description}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
                  {plan.limit} yogas
                </Typography>
                {plan.name === currentPlan.name ? (
                  <Chip label="Current" color="success" size="small" sx={{ mt: 1 }} />
                ) : (
                  <Button
                    variant="contained"
                    color={plan.color === 'default' ? 'primary' : plan.color}
                    size="large"
                    sx={{
                      mt: 1,
                      background: plan.name === 'Premium'
                        ? `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`
                        : undefined,
                      color: plan.name === 'Premium' ? theme.palette.common.white : undefined,
                      fontWeight: 700,
                      boxShadow: 2,
                    }}
                  >
                    {plan.price > currentPlan.price ? 'Upgrade' : 'Downgrade'}
                  </Button>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>


        {/* Current Plan & Usage */}
        <Grid container spacing={3} sx={{ mb: 4, mt: 4 }}>
          <Grid item xs={12} md={6}>
            <MainCard title="Current Plan" sx={{ height: '100%', background: theme.palette.background.paper, borderRadius: 3 }}>
              <Stack spacing={2} alignItems="flex-start">
                <Chip label={currentPlan.name} color={currentPlan.color} size="medium" />
                <Typography variant="body1">{currentPlan.description}</Typography>
                <Box sx={{ width: '100%' }}>
                  <Typography variant="caption" color="text.secondary">
                    Usage: {usage} / {currentPlan.limit} yogas
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={(usage / currentPlan.limit) * 100}
                    color={usage >= currentPlan.limit ? 'error' : 'primary'}
                    sx={{ mt: 1, height: 8, borderRadius: 5 }}
                  />
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" color="primary" disabled={currentPlan.name === 'Free'}>
                    Downgrade
                  </Button>
                  <Button variant="contained" color="error">
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            </MainCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <MainCard title="Subscription History" sx={{ height: '100%', background: theme.palette.background.paper, borderRadius: 3 }}>
              <TableContainer component={Paper} sx={{ background: 'transparent', boxShadow: 'none' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Plan</TableCell>
                      <TableCell>Start Date</TableCell>
                      <TableCell>End Date</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subscriptionHistory.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{row.plan}</TableCell>
                        <TableCell>{row.start}</TableCell>
                        <TableCell>{row.end}</TableCell>
                        <TableCell>
                          <Chip
                            label={row.status}
                            color={row.status === 'Active' ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </MainCard>
          </Grid>
        </Grid>

        {/* Support Message Section */}
        <Box sx={{ mb: 6 }}>
          <MainCard title="Have questions about your plan? Message us!" sx={{ background: theme.palette.background.paper, borderRadius: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <TextField
                label="Type your message..."
                variant="outlined"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                fullWidth
                multiline
                minRows={1}
                maxRows={4}
                sx={{ flex: 1 }}
                disabled={sent}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleSend}
                disabled={!message.trim() || sent}
                sx={{ minWidth: 120, fontWeight: 700 }}
              >
                {sent ? 'Sent!' : 'Send'}
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Our support team will get back to you as soon as possible.
            </Typography>
          </MainCard>
        </Box>
      </Box>
    </Box>
  );
}

export default Subscription;